import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Spin, Alert, Tag, Table, Button, Typography } from 'antd';
import {
    ClockCircleOutlined,
    ToolOutlined,
    CheckCircleOutlined,
    CarOutlined,
    DollarOutlined,
    WarningOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import api from '../../services/api';

const { Title } = Typography;

const LabDashboard = () => {
    const { t } = useTranslation(['laboratory', 'common']);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [stats, setStats] = useState({
        pending: 0,
        inProgress: 0,
        completed: 0,
        delivered: 0,
        overdue: 0,
        revenueIQD: 0,
        revenueUSD: 0
    });
    const [orders, setOrders] = useState([]);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);

            const ordersRes = await api.get('/lab/orders');
            const allOrders = ordersRes.data?.data || [];

            setOrders(allOrders.slice(0, 10));

            // Calculate revenue from completed/delivered orders
            const completedOrders = allOrders.filter(o => o.status === 'Completed' || o.status === 'Delivered');
            const revenueIQD = completedOrders.filter(o => o.currency === 'IQD').reduce((sum, o) => sum + (o.cost || 0), 0);
            const revenueUSD = completedOrders.filter(o => o.currency === 'USD').reduce((sum, o) => sum + (o.cost || 0), 0);

            // Check overdue orders
            const now = new Date();
            const overdueCount = allOrders.filter(o =>
                o.expectedDeliveryDate &&
                new Date(o.expectedDeliveryDate) < now &&
                !['Completed', 'Delivered', 'Cancelled'].includes(o.status)
            ).length;

            setStats({
                pending: allOrders.filter(o => o.status === 'Pending').length,
                inProgress: allOrders.filter(o => o.status === 'In Progress').length,
                completed: allOrders.filter(o => o.status === 'Completed').length,
                delivered: allOrders.filter(o => o.status === 'Delivered').length,
                overdue: overdueCount,
                revenueIQD,
                revenueUSD
            });

            setError('');
        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError(err.response?.data?.message || t('laboratory:messages.dashboardFetchFailed'));
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Pending': return 'orange';
            case 'In Progress': return 'blue';
            case 'Completed': return 'green';
            case 'Delivered': return 'cyan';
            case 'Cancelled': return 'red';
            default: return 'default';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'Urgent': return 'red';
            case 'Emergency': return 'magenta';
            default: return 'blue';
        }
    };

    const columns = [
        {
            title: t('laboratory:orders.orderNumber'),
            dataIndex: 'orderNumber',
            key: 'orderNumber',
        },
        {
            title: t('laboratory:orders.patient'),
            key: 'patient',
            render: (_, record) => record.patientId?.name || record.patientName || 'N/A'
        },
        {
            title: t('laboratory:orders.type'),
            dataIndex: 'orderType',
            key: 'orderType',
        },
        {
            title: t('laboratory:orders.priority'),
            dataIndex: 'priority',
            key: 'priority',
            render: (priority) => (
                <Tag color={getPriorityColor(priority)}>{t(`laboratory:orders.priorities.${priority}`, priority)}</Tag>
            )
        },
        {
            title: t('laboratory:orders.status'),
            dataIndex: 'status',
            key: 'status',
            render: (status) => <Tag color={getStatusColor(status)}>{t(`laboratory:orders.statuses.${status}`, status)}</Tag>
        },
        {
            title: t('laboratory:orders.expectedDelivery'),
            dataIndex: 'expectedDeliveryDate',
            key: 'expectedDelivery',
            render: (date) => date ? dayjs(date).format('MMM DD, YYYY') : '-'
        },
        {
            title: t('laboratory:orders.cost'),
            dataIndex: 'cost',
            key: 'cost',
            render: (cost, record) => {
                if (!cost) return '-';
                return record.currency === 'IQD'
                    ? `${cost.toLocaleString()} IQD`
                    : `$${cost.toFixed(2)}`;
            }
        },
    ];

    if (loading) {
        return (
            <div style={{ textAlign: 'center', marginTop: 100 }}>
                <Spin size="large" tip={t('loading')} />
            </div>
        );
    }

    return (
        <div style={{ padding: 24 }}>
            <Title level={2}>{t('laboratory:dashboard.title')}</Title>

            {error && (
                <Alert
                    message={t('common:error', 'Error')}
                    description={error}
                    type="error"
                    showIcon
                    style={{ marginBottom: 24 }}
                />
            )}

            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title={t('laboratory:dashboard.pendingOrders')}
                            value={stats.pending}
                            prefix={<ClockCircleOutlined />}
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title={t('laboratory:dashboard.inProgress')}
                            value={stats.inProgress}
                            prefix={<ToolOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title={t('laboratory:dashboard.completed')}
                            value={stats.completed}
                            prefix={<CheckCircleOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title={t('laboratory:dashboard.delivered')}
                            value={stats.delivered}
                            prefix={<CarOutlined />}
                            valueStyle={{ color: '#13c2c2' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} lg={8}>
                    <Card>
                        <Statistic
                            title={t('laboratory:dashboard.overdueOrders')}
                            value={stats.overdue}
                            prefix={<WarningOutlined />}
                            valueStyle={{ color: stats.overdue > 0 ? '#ff4d4f' : '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <Card>
                        <Statistic
                            title={t('laboratory:dashboard.revenueUSD')}
                            value={stats.revenueUSD}
                            prefix={<DollarOutlined />}
                            valueStyle={{ color: '#722ed1' }}
                            precision={2}
                            formatter={(value) => `$${Number(value).toFixed(2)}`}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <Card>
                        <Statistic
                            title={t('laboratory:dashboard.revenueIQD')}
                            value={stats.revenueIQD}
                            prefix={<DollarOutlined />}
                            valueStyle={{ color: '#722ed1' }}
                            formatter={(value) => `${Number(value).toLocaleString()} IQD`}
                        />
                    </Card>
                </Col>
            </Row>

            <Card
                title={t('laboratory:dashboard.recentOrders')}
                style={{ marginBottom: 24 }}
                extra={
                    <Button type="primary" onClick={() => navigate('/laboratory/orders')}>
                        {t('laboratory:dashboard.viewAllOrders')}
                    </Button>
                }
            >
                <Table
                    columns={columns}
                    dataSource={orders}
                    rowKey="_id"
                    pagination={false}
                    locale={{ emptyText: t('laboratory:dashboard.noOrdersFound') }}
                    scroll={{ x: 800 }}
                />
            </Card>
        </div>
    );
};

export default LabDashboard;
